/**
 * Parses confirm-step 422 responses into preview inline field errors.
 * Keeps ValidationService.js untouched; pairs with excel-import-preview.js.
 */
const DEFAULT_PAYLOAD_PREFIXES = ["preview", "to_create", "items", "courses"];

const FORM_FIELD_KEYS = [
  "academic_year_id",
  "target_academic_year_id",
  "file",
  "file_name",
];

const ROW_MESSAGE_RE = /^(?:Preview row|Row)\s+(\d+)\s*:\s*(.*)$/i;
const SLOT_MESSAGE_RE = /^(?:Preview row|Row)\s+(\d+)\s*:.*\bat item\s+(\d+)\b/i;

const MESSAGE_FIELD_KEYWORDS = [
  ["grade name", "grade_name"],
  // Course confirm: "Subject Teacher assignment for Teacher '...' teaching Subject '...'"
  ["subject teacher", "subject_name"],
  ["subject name", "subject_name"],
  ["annual fee", "annual_fee"],
  ["termly fee", "termly_fee"],
  ["monthly fee", "monthly_fee"],
  ["class name", "class_name"],
  ["teacher name", "teacher_name"],
  ["teacher no", "teacher_no"],
  ["exam start date", "exam_start_date"],
  ["exam end date", "exam_end_date"],
  ["pass mark", "pass_mark"],
  ["distinction mark", "distinction_mark"],
  ["full mark", "full_mark"],
  ["term exam rate", "term_exam_rate"],
  ["unit test rate", "unit_test_rate"],
  ["project rate", "project_rate"],
  ["payment item name", "p_item_name"],
  ["item name", "p_item_name"],
  ["payment item amount", "p_item_amount"],
  ["item amount", "p_item_amount"],
  ["item type", "item_type"],
  ["transaction category", "transaction_category_id"],
  ["grade assignment", "campus_grade_ids"],
  ["homeroom teacher", "homeroom_teacher_name"],
  ["assistant teacher", "assistant_teacher_names"],
  ["student name", "student_name"],
  ["payment plan", "payment_plan_id"],
  ["payment date", "payment_date"],
  ["payment method", "payment_method_id"],
  ["first calculation date", "first_calculation_date"],
  ["first attendance date", "first_attendance_date"],
  ["receiver", "receiver"],
  ["exam name", "exam_name"],
  ["exam type", "exam_type_label"],
  ["term name", "term_name"],
  ["send date", "send_date"],
  ["start date", "start_date"],
  ["end date", "end_date"],
  ["section", "section"],
  ["description", "description"],
  ["remark", "remark"],
];

function stripRowPrefix(message) {
  return String(message).replace(/^(?:Preview row|Row)\s+\d+:\s*/i, "").trim();
}

function inferFieldFromMessage(message) {
  const lower = String(message).toLowerCase();

  if (lower.includes("invalid day/time slot") || lower.includes("day/time slot")) {
    const slotMatch = message.match(/\bat item\s+(\d+)\b/i);
    if (slotMatch) {
      return `slot_${Math.max(0, parseInt(slotMatch[1], 10) - 1)}`;
    }
    return "slot_0";
  }

  if (lower.includes("pass mark")) {
    return "pass_mark";
  }
  if (lower.includes("distinction mark")) {
    return "distinction_mark";
  }
  if (lower.includes("full mark")) {
    return "full_mark";
  }
  if (lower.includes("must sum to 100")) {
    return "term_exam_rate";
  }

  for (let i = 0; i < MESSAGE_FIELD_KEYWORDS.length; i += 1) {
    const [keyword, field] = MESSAGE_FIELD_KEYWORDS[i];
    if (lower.includes(keyword)) {
      return field;
    }
  }

  return null;
}

function cloneFieldErrors(fieldErrors) {
  const cloned = {};

  Object.entries(fieldErrors || {}).forEach(([rowIndex, fields]) => {
    if (!fields || typeof fields !== "object") {
      return;
    }
    cloned[rowIndex] = { ...fields };
  });

  return cloned;
}

function parseDottedKeyErrors(errors, prefixes) {
  const fieldErrors = {};

  if (!errors || Array.isArray(errors) || typeof errors !== "object") {
    return fieldErrors;
  }

  Object.entries(errors).forEach(([key, messages]) => {
    const message = Array.isArray(messages) ? messages[0] : messages;
    if (!message) {
      return;
    }

    prefixes.forEach((prefix) => {
      const match = key.match(new RegExp(`^${prefix}\\.(\\d+)\\.(.+)$`));
      if (!match) {
        return;
      }

      const rowIndex = match[1];
      const field = match[2];
      if (!fieldErrors[rowIndex]) {
        fieldErrors[rowIndex] = {};
      }
      fieldErrors[rowIndex][field] = String(message);
    });
  });

  return fieldErrors;
}

function parseFlatMessageErrors(errors) {
  const fieldErrors = {};

  if (!Array.isArray(errors)) {
    return fieldErrors;
  }

  errors.forEach((rawMessage) => {
    const message = String(rawMessage || "");
    if (!message) {
      return;
    }

    const slotMatch = message.match(SLOT_MESSAGE_RE);
    if (slotMatch) {
      const rowIndex = String(Math.max(0, parseInt(slotMatch[1], 10) - 1));
      const slotIndex = Math.max(0, parseInt(slotMatch[2], 10) - 1);
      if (!fieldErrors[rowIndex]) {
        fieldErrors[rowIndex] = {};
      }
      fieldErrors[rowIndex][`slot_${slotIndex}`] = stripRowPrefix(message);
      return;
    }

    const rowMatch = message.match(ROW_MESSAGE_RE);
    if (!rowMatch) {
      return;
    }

    const rowIndex = String(Math.max(0, parseInt(rowMatch[1], 10) - 1));
    const field = inferFieldFromMessage(message);
    if (!field) {
      return;
    }

    if (!fieldErrors[rowIndex]) {
      fieldErrors[rowIndex] = {};
    }
    fieldErrors[rowIndex][field] = stripRowPrefix(message);
  });

  return fieldErrors;
}

function mergeFieldErrors(target, source) {
  const merged = cloneFieldErrors(target);

  Object.entries(source || {}).forEach(([rowIndex, fields]) => {
    if (!fields || typeof fields !== "object") {
      return;
    }
    merged[rowIndex] = {
      ...(merged[rowIndex] || {}),
      ...fields,
    };
  });

  return merged;
}

function expandRateSumErrors(fieldErrors) {
  const expanded = cloneFieldErrors(fieldErrors);

  Object.entries(expanded).forEach(([rowIndex, fields]) => {
    const hasSumError = Object.values(fields).some(
      (message) => message && /must sum to 100/i.test(String(message))
    );

    if (!hasSumError) {
      return;
    }

    const sumMessage = Object.values(fields).find(
      (message) => message && /must sum to 100/i.test(String(message))
    );

    expanded[rowIndex] = {
      ...fields,
      term_exam_rate: sumMessage,
      unit_test_rate: sumMessage,
      project_rate: sumMessage,
    };
  });

  return expanded;
}

export default {
  methods: {
    /**
     * Parse confirm 422 payload into preview row field errors.
     *
     * @param {Object} response - Axios error response (status 422).
     * @param {Object} options - Module-specific parser options.
     * @returns {Object} Field errors keyed by preview row index.
     */
    printImportConfirmErrors(response, options = {}) {
      const data = response?.data || {};
      const prefixes = options.payloadPrefixes || DEFAULT_PAYLOAD_PREFIXES;

      let fieldErrors = cloneFieldErrors(data.field_errors || {});
      fieldErrors = mergeFieldErrors(fieldErrors, parseDottedKeyErrors(data.errors, prefixes));
      fieldErrors = mergeFieldErrors(fieldErrors, parseFlatMessageErrors(data.errors));

      if (options.rowKey) {
        fieldErrors = this.resolveImportRowKeyErrors(fieldErrors, options.rowKey);
      }

      fieldErrors = expandRateSumErrors(fieldErrors);

      this.applyImportFormValidationErrors(data.errors);

      return fieldErrors;
    },

    resolveImportRowKeyErrors(fieldErrors, rowKey) {
      // Course uses previewData; other modules use previewRows.
      const previewRows = this.previewRows || this.previewData || [];
      if (!previewRows.length) {
        return fieldErrors;
      }

      const resolved = {};

      Object.entries(fieldErrors || {}).forEach(([key, fields]) => {
        const idxByRowKey = previewRows.findIndex(
          (row) => String(row[rowKey]) === String(key)
        );
        const numericKey = Number(key);
        let targetIdx = key;

        if (idxByRowKey >= 0) {
          targetIdx = idxByRowKey;
        } else if (
          !Number.isNaN(numericKey)
          && numericKey >= 0
          && numericKey < previewRows.length
        ) {
          targetIdx = numericKey;
        }

        resolved[targetIdx] = {
          ...(resolved[targetIdx] || {}),
          ...fields,
        };
      });

      return resolved;
    },

    applyImportFormValidationErrors(errors) {
      if (!errors || Array.isArray(errors) || typeof errors !== "object") {
        return;
      }

      const formErrors = { ...(this.validationErrors || {}) };

      Object.entries(errors).forEach(([key, messages]) => {
        if (!FORM_FIELD_KEYS.includes(key)) {
          return;
        }

        formErrors[key] = Array.isArray(messages) ? messages : [String(messages)];
      });

      this.validationErrors = formErrors;
    },

    clearImportConfirmErrors() {
      this.clearConfirmFieldErrors();
    },
  },
};
